'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { useEditDrawerData } from 'src/hooks/common/useEditDrawerData';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faBookmark,
  faCheck,
  faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import { ParentFilterGroup } from 'src/context/reducers/filterSetReducer';
import {
  SITE_OF_CARE_DISPLAY_TYPE,
  collectSiteOfCareLabels,
  countAppliedFilterValues,
  isSocFilterType,
  isSocSuppressedFilterType,
} from 'src/components/FilterSets/SiteOfCare/displayHelpers';
import { useSocOptions } from 'src/components/FilterSets/SiteOfCare';
import { SectionHeader } from '../Section';

const sections = ['PERSONAL', 'COMPANY'];

const BillingDropdown = ({ location }: { location: string }) => {
  const filterKey = 'volume';
  const displayName = 'Billing';
  const typeMap = {
    PAYER_TYPE: 'Payer Type',
    PLACE_OF_SERVICE: 'Place of Service',
    SOC_TYPE: SITE_OF_CARE_DISPLAY_TYPE,
    SOC_SUBTYPE: SITE_OF_CARE_DISPLAY_TYPE,
  };

  const { groupedFilterSets, groupedByScope } = useFilterSetUtils({
    filterType: 'FILTER',
  });
  const { closeAllButCurrent } = useDrawer();
  // Use the table filter selections hook
  const {
    billing,
    tempSelectedFilterPreset,
    selectedFilterPreset,
    setTempSelectedFilterPreset,
  } = useTableFilterSelections(location);
  const { setCurrentFilterSetData } = useEditDrawerData();
  const { socOptions } = useSocOptions();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Flatten the grouped filter sets into an array
  const filterSetsList: ParentFilterGroup[] = useMemo(
    () =>
      Object.values(groupedFilterSets || {})
        .flat()
        .map((fs) => fs as unknown as ParentFilterGroup),
    [groupedFilterSets]
  );

  // Find the selected filter set objects
  const selectedFilterSets = useMemo(() => {
    if (!filterSetsList.length) return [];

    return filterSetsList.filter((fs) => billing.includes(fs.id));
  }, [billing, filterSetsList]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const label = useMemo(() => {
    if (tempSelectedFilterPreset) {
      return tempSelectedFilterPreset.label;
    }

    if (selectedFilterSets.length === 0) {
      return 'Select billing preset';
    }

    return selectedFilterSets.map((fs) => fs.label).join(' and ');
  }, [tempSelectedFilterPreset, selectedFilterSets]);

  const handleToggleFilterSet = (filterSet: ParentFilterGroup) => {
    // Check if this preset is already selected
    const isSelected = tempSelectedFilterPreset?.label === filterSet.label;

    if (isSelected) {
      // If already selected, clear the temp preset only
      setTempSelectedFilterPreset(null);
    } else {
      // Select the new preset in temp state only
      setTempSelectedFilterPreset(filterSet);
    }
  };

  const handleEditFilterSet = (
    e: React.MouseEvent<HTMLDivElement>,
    filterSet: ParentFilterGroup
  ) => {
    //TODO: will come back to this.
    e.stopPropagation();
    setCurrentFilterSetData(filterSet);
    closeAllButCurrent(`editFilterDrawer-${location}`);
  };

  // Total selected values. countAppliedFilterValues collapses the SOC_TYPE /
  // SOC_SUBTYPE pair, which a single Site of Care picker emits as two rows.
  const filterCount = useMemo(() => {
    const countFilterValues = (preset: any) =>
      Array.isArray(preset?.filters)
        ? countAppliedFilterValues(preset.filters)
        : 0;

    // Count from applied billing preset, but prioritize temp if it exists (for immediate UI updates)
    if (tempSelectedFilterPreset) {
      return countFilterValues(tempSelectedFilterPreset);
    }

    // Count from applied billing preset
    if (selectedFilterPreset) {
      return countFilterValues(selectedFilterPreset);
    }

    // If no preset is selected, count from billing array (individual filter selections)
    if (billing && billing.length > 0) {
      return billing.length;
    }

    return 0;
  }, [selectedFilterPreset, tempSelectedFilterPreset, billing]);

  const filterVerbiage = useMemo(() => {
    if (!tempSelectedFilterPreset) return null;

    // Group all filters by type
    const filtersByType: Record<string, string[]> = {};

    // Use detailed_filters if available, otherwise fall back to filters
    if (
      tempSelectedFilterPreset.detailed_filters &&
      tempSelectedFilterPreset.detailed_filters.length > 0
    ) {
      const siteOfCareLabels = collectSiteOfCareLabels(
        tempSelectedFilterPreset.detailed_filters,
        socOptions
      );
      if (siteOfCareLabels.length > 0) {
        filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
      }

      // Use detailed_filters with detailed_values
      tempSelectedFilterPreset.detailed_filters.forEach((filter) => {
        const type = filter.type;
        if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
        const displayType = typeMap[type as keyof typeof typeMap] || type;

        if (!filter.detailed_values || filter.detailed_values.length === 0) {
          return;
        }

        // Initialize the array for this type if it doesn't exist
        if (!filtersByType[displayType]) {
          filtersByType[displayType] = [];
        }

        // Add all labels from detailed_values to the array for this type
        filter.detailed_values.forEach((detailedValue) => {
          if (!filtersByType[displayType].includes(detailedValue.label)) {
            filtersByType[displayType].push(detailedValue.label);
          }
        });
      });
    } else if (
      tempSelectedFilterPreset.filters &&
      tempSelectedFilterPreset.filters.length > 0
    ) {
      const siteOfCareLabels = collectSiteOfCareLabels(
        tempSelectedFilterPreset.filters,
        socOptions
      );
      if (siteOfCareLabels.length > 0) {
        filtersByType[SITE_OF_CARE_DISPLAY_TYPE] = siteOfCareLabels;
      }

      // Use regular filters with values
      tempSelectedFilterPreset.filters.forEach((filter) => {
        const type = filter.type;
        if (isSocFilterType(type) || isSocSuppressedFilterType(type)) return;
        const displayType = typeMap[type as keyof typeof typeMap] || type;

        if (!filter.values || filter.values.length === 0) {
          return;
        }

        // Initialize the array for this type if it doesn't exist
        if (!filtersByType[displayType]) {
          filtersByType[displayType] = [];
        }

        // Add all values to the array for this type
        filter.values.forEach((value) => {
          const valueLabel = typeof value === 'string' ? value : value;
          if (!filtersByType[displayType].includes(valueLabel)) {
            filtersByType[displayType].push(valueLabel);
          }
        });
      });
    }

    // Convert the grouped filters to JSX
    const filterElements: React.JSX.Element[] = [];

    Object.entries(filtersByType).forEach(([type, labels], index) => {
      // Add separator if not the first item
      if (index > 0) {
        filterElements.push(
          <span
            key={`separator-${index}`}
            className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
          >
            ;{' '}
          </span>
        );
      }

      // Add type label
      filterElements.push(
        <span
          key={`type-${index}`}
          className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
        >
          {type} includes:{' '}
        </span>
      );

      // Add labels
      labels.forEach((label, labelIndex) => {
        // Add comma if not the first label
        if (labelIndex > 0) {
          filterElements.push(
            <span
              key={`comma-${index}-${labelIndex}`}
              className="text-tertiary text-xs font-normal font-['Inter'] leading-none"
            >
              ,{' '}
            </span>
          );
        }

        // Add label
        filterElements.push(
          <span
            key={`label-${index}-${labelIndex}`}
            className="text-tertiary text-xs font-bold font-['Inter'] leading-none"
          >
            {label}
          </span>
        );
      });
    });

    return filterElements;
  }, [tempSelectedFilterPreset, typeMap, socOptions]);

  return (
    <div
      className="relative self-stretch inline-flex flex-col justify-start items-start gap-1"
      ref={dropdownRef}
      data-testid={`${filterKey}-dropdown`}
    >
      <div className="self-stretch flex justify-start items-center">
        <div className="justify-start text-primary text-xs font-semibold font-['Inter'] leading-none">
          {displayName}
        </div>
      </div>

      {/* Dropdown Button */}
      <div
        className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1
        outline-neutral-300 inline-flex justify-between gap-2 items-center cursor-pointer"
        onClick={toggleDropdown}
      >
        <div className="flex items-center justify-between w-full">
          <div className="inline-flex justify-start items-center gap-2">
            <div className="w-5 h-5 overflow-hidden flex items-center justify-center">
              <Icon
                icon={faBookmark}
                className="w-2.5 h-3.5 text-neutral-600"
              />
            </div>
            <div className="justify-start text-primary text-base font-normal font-['Inter'] leading-normal">
              {label}
            </div>
          </div>
          <div className="px-2 py-1 bg-sky-50 rounded-full flex justify-center items-center gap-2 overflow-hidden">
            <div className="text-center justify-start text-sky-600 text-xs font-semibold font-['Inter'] leading-none">
              {filterCount}
            </div>
          </div>
        </div>
        <Icon icon={faCaretDown} className="w-4 h-4 text-neutral-600" />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="w-96 min-w-48 py-2 top-16 left-1/2 -translate-x-1/2 absolute bg-white
          rounded-lg shadow-md outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex
          flex-col justify-start items-start overflow-hidden z-50"
        >
          <div className="self-stretch max-h-64 flex flex-col justify-start items-start">
            {sections.map((section, index) => {
              const currentSection = groupedByScope[section];
              return (
                <div
                  key={`${section}-${index}`}
                  className="self-stretch flex flex-col justify-start items-start"
                >
                  <SectionHeader label={section} />
                  {currentSection?.length ? (
                    currentSection?.map((item, index) => {
                      const isItemSelected =
                        tempSelectedFilterPreset?.label === item.label;
                      return (
                        <div
                          key={`${item}-${index}`}
                          className="group self-stretch px-4 py-2 hover:bg-level-1 inline-flex justify-start items-center gap-2 cursor-pointer"
                          onClick={() =>
                            handleToggleFilterSet(item as ParentFilterGroup)
                          }
                        >
                          <div
                            className={`w-5 h-5 overflow-hidden flex items-center justify-center
                                  ${
                                    isItemSelected ? 'opacity-100' : 'opacity-0'
                                  }
                                `}
                          >
                            <Icon
                              icon={faCheck}
                              className="w-3.5 h-2.5 text-neutral-600"
                            />
                          </div>
                          <div className="flex-1 justify-start text-primary text-base font-normal font-['Inter'] leading-normal">
                            {item.label}
                          </div>

                          <div
                            className="w-5 h-5 overflow-hidden flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                            onClick={(e) =>
                              handleEditFilterSet(e, item as ParentFilterGroup)
                            }
                          >
                            <Icon
                              icon={faPencil}
                              className="w-3.5 h-3.5 text-neutral-400"
                            />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="group self-stretch px-4 py-2 hover:bg-level-1 inline-flex justify-start items-center gap-2 cursor-pointer">
                      <div className="flex-1 justify-start text-neutral-500 pl-7 text-base font-normal font-['Inter'] leading-normal">
                        No Filters Sets
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Filter Verbiage */}
      {filterVerbiage && (
        <div className="self-stretch justify-start">{filterVerbiage}</div>
      )}
    </div>
  );
};

export default BillingDropdown;
